import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import { Box, GlobalStyles } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CourierService from '../../services/courier/courierService';
import { CircularProgress } from '@mui/material';
import { useNotify } from 'react-admin';
import * as Sentry from '@sentry/react';
import { BackButton } from '../../components/back-button/BackButton';
import { PageHeader } from '../../components/page-header/PageHeader';
import { BRAND_PRIMARY } from '../../core/theme/theme';

const containerStyle = {
  // width: '100%',
  flex: 6,
  height: '75vh',
};

const center = {
  lat: 34.693,
  lng: 33.022,
};

export const OrdersMapScreen = () => {
  const [selectedCourier, setSelectedCourier] = useState<any>(null);
  const { t } = useTranslation();
  const notify = useNotify();
  const courierService = new CourierService();
  const mapRef = useRef<google.maps.Map | null>(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_PLACES_API_KEY!,
    libraries: ['places'],
  });

  const { data: couriers = [] } = useQuery({
    queryKey: ['couriers'],
    queryFn: async () => {
      try {
        const data = await courierService.getLastLocation();
        console.log('getLastLocation: ' + JSON.stringify(data));
        return data;
      } catch (error: any) {
        console.error('getLastLocation error:', error);
        notify(error?.response?.data?.error || t('notifications.internalError'), { type: 'error' });
        Sentry.captureException(error);
        return [];
      }
    },
    refetchInterval: 2000 * 60,
    refetchIntervalInBackground: true,
  });

  if (!isLoaded) {
    return (
      <Box
        sx={{
          height: '75vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <GlobalStyles
        styles={{
          '.gm-style .gm-style-iw': {
            background: 'transparent !important',
            boxShadow: 'none !important',
            border: 'none !important',
            padding: '0 !important',
            overflow: 'visible !important',
          },

          '.gm-style .gm-style-iw-t::after, .gm-style-iw-chr, .gm-style-iw-tc': {
            display: 'none !important',
          },

          '.gm-style-iw-d': {
            overflow: 'auto !important',
          },

          '.courier-tooltip': {
            background: 'var(--color-primary)',
            color: 'var(--color-text-inverse)',
            fontWeight: 600,
            fontSize: '13px',
            padding: '4px 10px',
            borderRadius: '999px',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          },
        }}
      />
      <Box>
        <PageHeader title={t('resources.orders')} back={<BackButton />} />
        <Box sx={{ display: 'flex', height: 'fit-content', px: 'var(--space-8)' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              height: '75vh',
              border: '1px solid #f0f0f0',
            }}
          >
            {couriers.map((courier: any) => (
              <Box
                key={courier.courierId}
                sx={{
                  whiteSpace: 'nowrap',
                  padding: '12px 16px',
                  borderBottom: '1px solid #f0f0f0',
                  fontSize: '14px',
                  color: '#111',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#f7f7f7',
                  },
                }}
                onClick={() => {
                  setSelectedCourier(courier);

                  if (mapRef.current) {
                    mapRef.current.setZoom(15);
                    mapRef.current.panTo({
                      lat: courier.latitude,
                      lng: courier.longitude,
                    });
                  }
                }}
              >
                {courier.courierName}
              </Box>
            ))}
          </Box>

          <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={13}
            onLoad={(map) => {
              mapRef.current = map;
            }}
            options={{
              disableDefaultUI: true,
              zoomControl: true,
              styles: [
                {
                  featureType: 'all',
                  stylers: [{ saturation: -100 }],
                },
              ],
            }}
          >
            {couriers.map((courier: any) => (
              <Marker
                key={courier.courierId}
                position={{
                  lat: courier.latitude,
                  lng: courier.longitude,
                }}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 8,
                  fillColor: BRAND_PRIMARY,
                  fillOpacity: 1,
                  strokeColor: '#000000',
                  strokeWeight: 2,
                }}
                onMouseOver={() => setSelectedCourier(courier)}
                onMouseOut={() => setSelectedCourier(null)}
              />
            ))}

            {selectedCourier && (
              <InfoWindow
                position={{
                  lat: selectedCourier.latitude,
                  lng: selectedCourier.longitude,
                }}
                options={{
                  pixelOffset: new window.google.maps.Size(0, -5),
                  disableAutoPan: true,
                }}
              >
                <div className="courier-tooltip">{selectedCourier.courierName}</div>
              </InfoWindow>
            )}
          </GoogleMap>
        </Box>
      </Box>
    </>
  );
};
